import AuthTeams from "../../auth/AuthTeams";
import { sanitizeFileName } from "../../functions/helperMethod";

const WORD_LIKE_EXTENSIONS = new Set(["DOC", "DOCX", "DOCM", "DOTM"]);

/** True for Word-family extensions that support auto-generated view copies. */
export function isWordLikeExtension(extension) {
  return WORD_LIKE_EXTENSIONS.has(String(extension ?? "").toUpperCase());
}

function firstNonEmpty(...values) {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text !== "") return text;
  }
  return "";
}

/**
 * Build a documentRevision object (the shape `check-in-existing` expects) from
 * the IFS details response merged with the shaped working-document row.
 * @param {object} revision - normalized IFS revision payload (may be partial)
 * @param {object} doc - shaped checked-out doc row (carries parsed identifiers)
 */
export function buildCheckoutRevision(revision, doc) {
  const rev = revision && typeof revision === "object" ? revision : {};
  const fileExtension = firstNonEmpty(
    rev.FileExtension,
    rev.fileExtension,
    doc?.fileExtension,
  );
  return {
    docClass: firstNonEmpty(rev.docClass, rev.DocClass, doc?.docClass),
    docNo: firstNonEmpty(rev.docNo, rev.DocNo, doc?.docNo),
    docRev: firstNonEmpty(rev.docRev, rev.DocRev, doc?.docRev),
    docSheet: firstNonEmpty(rev.docSheet, rev.DocSheet, doc?.docSheet, "1"),
    Title: firstNonEmpty(rev.Title, rev.title, doc?.title, doc?.name),
    FormatSize: firstNonEmpty(rev.FormatSize, rev.formatSize, doc?.formatSize),
    DocType: firstNonEmpty(rev.DocType, rev.docType),
    FileNo: firstNonEmpty(rev.FileNo, rev.fileNo, "1"),
    DocRespName: firstNonEmpty(rev.DocRespName, rev.docRespName, rev.Owner),
    FileExtension: fileExtension,
  };
}

/**
 * Fetch a URL and resolve to a RAW base64 string (no `data:…;base64,` prefix).
 * The check-in-existing backend decodes `fileData` directly (e.g. to generate a
 * PDF view copy), so the data-URL prefix must be stripped or it throws
 * "not a valid Base-64 string" — same format the New Document flow sends.
 */
export async function getBase64FromUrl(url) {
  const response = await fetch(url);
  const blob = await response.blob();
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        resolve("");
        return;
      }
      const commaIndex = result.indexOf(",");
      resolve(commaIndex >= 0 ? result.substring(commaIndex + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.readAsDataURL(blob);
  });
}

/**
 * Build a single `check-in-existing` request body. Mirrors the payload in
 * `onCheckinExistingDocument`. `docReleaseStatus` is the IFS status string
 * ("Released") or "" / false for none.
 * @returns {object} body with a client-only `__meta` for result correlation
 */
export function buildExistingCheckinBody({
  documentRevision,
  fileName,
  fileData,
  objectConnections = [],
  createViewCopy = false,
  docReleaseStatus = "",
  viewCopyFile = null,
  viewRequired = false,
  meta = {},
}) {
  const webUser = new AuthTeams().getEmail();
  const hasManualViewCopy =
    viewCopyFile != null &&
    typeof viewCopyFile.base64str === "string" &&
    viewCopyFile.base64str !== "";
  return {
    checkinViewCopy: false,
    docClass: documentRevision?.docClass ?? "",
    docNo: documentRevision?.docNo ?? "",
    docRev: documentRevision?.docRev ?? "",
    title: encodeURIComponent(documentRevision?.Title ?? ""),
    docFormat: documentRevision?.FormatSize ?? "",
    docSheet: documentRevision?.docSheet ?? "",
    fileData,
    fileName: sanitizeFileName(fileName ?? ""),
    objectConnections: Array.isArray(objectConnections) ? objectConnections : [],
    // Auto-generate and manual upload are mutually exclusive.
    createViewCopy: hasManualViewCopy ? false : createViewCopy === true,
    rollbackRevisionOnFail: false,
    webUser,
    status: docReleaseStatus === false ? "" : (docReleaseStatus ?? ""),
    __meta: meta,
    ...(hasManualViewCopy
      ? {
          __viewCopy: {
            name: viewCopyFile.name,
            base64str: viewCopyFile.base64str,
            title: documentRevision?.Title ?? "",
            viewRequired: viewRequired === true,
          },
        }
      : {}),
  };
}
